import isPropValid from '@emotion/is-prop-valid';
import { HelmetProvider } from 'react-helmet-async';
import { StyleSheetManager } from 'styled-components';

import { AlertDialogProvider } from '@creditornot/alchemy-react';
import { registerAuthInterceptor } from '@creditornot/wolt-auth/axios';
import { AuthProvider } from '@creditornot/wolt-auth/react';
import { assetRepositoryApiAgent, gatewayApiAgent } from 'common/api/apiAgents';
import { authenticator } from 'common/auth/authenticator';
import { ThemeProvider } from 'common/components/cookbook/ThemeProvider';
import { QueryProviders } from 'common/query/QueryProviders';

import { ErrorBoundary } from './ErrorBoundary';
import { IntlProvider } from './IntlProvider';
import { RouterProvider } from './RouterProvider/RouterProvider';
import { useCaptureBeforeInstallEvent } from '../service-worker/useCaptureBeforeInstallEvent';
import { avoInit } from './telemetry/avo/avoInit';

import './global.css';
import '@creditornot/alchemy-react/reset-soft.css';
import '@creditornot/alchemy-react/style.css';
import '@creditornot/alchemy-react/theme/default.css';
import '@creditornot/cb-ingredients/theme/default.css';

// This is needed to pass the correct authenticator type to the React hooks:
declare module '@creditornot/wolt-auth/react' {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface AuthContext {
    authenticator: typeof authenticator;
  }
}

const isProduction = process.env.NODE_ENV === 'production';

avoInit();

registerAuthInterceptor(authenticator, gatewayApiAgent, assetRepositoryApiAgent);

export const App = () => {
  useCaptureBeforeInstallEvent();

  return (
    <ThemeProvider>
      <ErrorBoundary>
        <HelmetProvider>
          <StyleSheetManager
            shouldForwardProp={(propName, elementToBeRendered) => {
              return typeof elementToBeRendered === 'string' ? isPropValid(propName) : true;
            }}
            enableVendorPrefixes={isProduction}
          >
            <QueryProviders>
              <IntlProvider>
                <AlertDialogProvider>
                  <AuthProvider authenticator={authenticator}>
                    <RouterProvider />
                  </AuthProvider>
                </AlertDialogProvider>
              </IntlProvider>
            </QueryProviders>
          </StyleSheetManager>
        </HelmetProvider>
      </ErrorBoundary>
    </ThemeProvider>
  );
};
